/**
 * Created by Alican Köker
 * on 2018/02/19.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {TimelineLite, TweenLite, Linear} from "gsap";

//configuration
export interface SlidingNumberViewData {
    imageName:string,
    digits:number,
    imageHeight:number,
    value?:number,
    imageLeftMargin?:number,
    imageTopMargin?:number,
    digitPadding?:number,
    animationDuration?:number,
    maskWidth?:number,
    maskHeight?:number,
    maskX?:number,
    maskY?:number,
}

export class SlidingNumberView extends BaseView {

    private _stripeArr:PIXI.Sprite[];
    private _sNData:SlidingNumberViewData;
    private _container:PIXI.Container;
    private _timeline:TimelineLite;
    private _tweenArr:TweenLite[];

    private _value:number = 0;
    private _initialValue:number = 0;
    private _frameHeight:number;
    private _digits:number;
    private _imageTopMargin:number;
    private _imageLeftMargin:number;
    private _digitPadding:number;
    private _animationDuration:number;
    private _maskWidth:number;
    private _maskHeight:number;
    private _maskX:number;
    private _maskY:number;

    constructor(data:SlidingNumberViewData) {
        super();

        this._sNData = data;
        this._frameHeight = this._sNData.imageHeight / 11;
        this._container = new PIXI.Container();
        this._timeline = new TimelineLite();
        this._value = this._sNData.value ? this._sNData.value : 0;
        this._digits = this._sNData.digits ? this._sNData.digits : this._value.toString().length;
        this._imageTopMargin = this._sNData.imageTopMargin ? this._sNData.imageTopMargin : 0;
        this._imageLeftMargin = this._sNData.imageLeftMargin ? this._sNData.imageLeftMargin : 0;
        this._digitPadding = this._sNData.digitPadding ? this._sNData.digitPadding : 0;
        this._animationDuration = this._sNData.animationDuration ? this._sNData.animationDuration : 0;
        this._maskWidth = this._sNData.maskWidth ? this._sNData.maskWidth : 0;
        this._maskHeight = this._sNData.maskHeight ? this._sNData.maskHeight : this._frameHeight;
        this._maskX = this._sNData.maskX ? this._sNData.maskX : 0;
        this._maskY = this._sNData.maskY ? this._sNData.maskY : 0;


        if(this._sNData.value) {
            this._value = this._sNData.value;
            this._initialValue = this._sNData.value;
        }
        else {
            this._value = 0;
            this._initialValue = 0;
        }
    }

    protected initAnimations():void {
        this._stripeArr = [];
        this._tweenArr = new Array<TweenLite>(this._digits);

        for(let i:number = 0; i < this._digits; i++){
            this._stripeArr.push(new PIXI.Sprite(GameResources.getTextures(this._sNData.imageName)[0]));
            this._stripeArr[i].position.set((this._digits - i - 1) * (this._stripeArr[0].width + this._digitPadding), -10 * this._frameHeight);
            this._container.addChild(this._stripeArr[i]);
        }

        this._maskWidth = this._sNData.maskWidth ? this._sNData.maskWidth :
            this._stripeArr[0].width * (this._sNData.digits + this._digitPadding);

        this.setNumber(this._value);
        let mask:PIXI.Graphics = new PIXI.Graphics();
        mask.beginFill(0xa00000);
        mask.drawRect(this._maskX, this._maskY, this._maskWidth, this._maskHeight);
        mask.endFill();
        this.container.addChild(mask);

        this._container.mask = mask;

    }

    public setNumber(val:number, callback?:{}, callbackVars?:any[]):void {
        this._tweenArr = [];
        let numStr:string = val.toString();
        if(val.toString().length < this._digits){
            numStr = "";
            for(let i = 0; i < this._digits - val.toString().length; i++){
                numStr += "0";
            }
            numStr += val.toString();
        }

        for(let i = this._digits - 1; i > -1 ; i--) {
            this.animateDigit(i, Number(numStr[i]), callback, callbackVars);
        }
        this._value = val;
    }

    private animateDigit(digit:number, value:number, callback?:{}, callbackVars?:any[]):void {

        if(this._tweenArr) {
            this._tweenArr.forEach(
                t => {
                    t.pause();
                    t.kill();
                }
            )
        }
        //0-1, 0-9 transitions are special cases since there must be two zeros on the both ends of the stripe image
        if(this._stripeArr) {
            if(value === 0) {
                //case when the number decremented from 1 to 0
                if(this._stripeArr[this._digits - digit - 1].y < this._frameHeight * -8) {
                    this._tweenArr.push(new TweenLite(this._stripeArr[this._digits - digit - 1], this._animationDuration,
                        {
                            y : (this._frameHeight) * (value - 10) + this._imageTopMargin,
                            onComplete : callback,
                            onCompleteVars : [callbackVars],
                            ease : Linear.easeNone,
                        }));
                }
                //case when the number is incremented from 9 to 0 (10)
                else {
                    this._tweenArr.push(new TweenLite(this._stripeArr[this._digits - digit - 1], this._animationDuration,
                        {
                            y : this._imageTopMargin,
                            onComplete : callback,
                            onCompleteVars : [callbackVars],
                            ease : Linear.easeNone,
                        }));
                }
            }
            //prevents stripe moving to 1 on wrong direction
            else if(value === 1 && this._stripeArr[this._digits - digit - 1].y > this._frameHeight * -8) {
                this._stripeArr[this._digits - digit - 1].position.y = -this._frameHeight * 10;
                this._tweenArr.push(new TweenLite(this._stripeArr[this._digits - digit - 1], this._animationDuration,
                    {
                        y : -this._frameHeight * 9,
                        onComplete : callback,
                        onCompleteVars : [callbackVars],
                        ease : Linear.easeNone,
                    }));
            }
            //prevents stripe moving to 9 from wrong direction
            else if(value === 9 && this._stripeArr[this._digits - digit - 1].y < this._frameHeight * -8) {
                this._stripeArr[this._digits - digit - 1].position.y = this._imageTopMargin;
                this._tweenArr.push(new TweenLite(this._stripeArr[this._digits - digit - 1], this._animationDuration,
                    {
                        y : -this._frameHeight,
                        onComplete : callback,
                        onCompleteVars : [callbackVars],
                        ease : Linear.easeNone,
                    }));
            }
            //rest of the numbers
            else {
                this._tweenArr.push(new TweenLite(this._stripeArr[this._digits - digit - 1], this._animationDuration,
                    {
                        y : (this._frameHeight) * (value - 10),
                        onComplete : callback,
                        onCompleteVars : [callbackVars],
                        ease : Linear.easeNone,
                    }));
            }
        }

        this._tweenArr.forEach(
            d =>{
                d.play();
            }
        )
    }

    public get container():PIXI.Container {
        return this._container;
    }
}